import type { MutableRefObject } from 'react'
import { toast } from 'sonner'
import api from '@/services/api'
import type { ComboVariation, Deal, PropostaItem } from '@/types'
import { formatCurrency } from '@/utils/format-currency'
import { getFriendlyError } from '@/utils/getFriendlyError'
import type { LayoutConfig } from '@/utils/dealCalculations'
import {
  findCatalogPlanForItem,
  getScopeFromCatalog,
  remapCategoryCoordsToScope,
  type CatalogPlan,
} from '@/utils/planCatalog'
import { normalizeItemSetupEntry } from '@/utils/recurringSetupEntry'

export function buildFinalLayoutForSave(
  layout: LayoutConfig,
  activeP: PropostaItem | null,
): LayoutConfig {
  return {
    ...layout,
    servicesCol1X: layout.serviceBlockX,
    servicesCol2X: layout.serviceBlockGap,
    servicesY: layout.serviceBlockStartY,
    descY: layout.descriptionY,
    bottomY: layout.loyaltyY,
    planName: activeP ? activeP.descricao || activeP.nome || '' : '',
    description: activeP ? activeP.description || activeP.descricao || '' : '',
    paymentDetails: activeP?.pricing?.recorrente
      ? ''
      : activeP
        ? activeP.pricing?.installmentValue
          ? `${activeP.pricing.installments}x de ${formatCurrency(activeP.pricing.installmentValue)}`
          : `${formatCurrency(activeP.valorUnitario)}`
        : '',
    loyaltyDuration: activeP
      ? activeP.pricing?.recorrente
        ? `Fidelidade: ${activeP.duration || 12} meses`
        : `Duração: ${activeP.duration || 1} meses`
      : '',
    bonusText: activeP && activeP.showBonus === true ? activeP.bonus || '' : '',
  } as LayoutConfig
}

export async function saveLayoutAsPlanDefault(params: {
  proposalItems: PropostaItem[]
  activeIndex: number
  catalogPlans: CatalogPlan[]
  layout: LayoutConfig
  scopeLayoutSignatureRef: MutableRefObject<string>
  reloadCatalog: (options?: { silent?: boolean }) => Promise<void>
}): Promise<void> {
  const item = params.proposalItems[params.activeIndex]
  if (!item) return
  const master = findCatalogPlanForItem(item, params.catalogPlans)
  if (!master?.id) {
    toast.error('Vincule um plano do catálogo antes de salvar o layout padrão.')
    return
  }
  const scope = getScopeFromCatalog(master)
  const categoryCoords = remapCategoryCoordsToScope(scope, params.layout.categoryCoords)
  if (Object.keys(categoryCoords).length === 0) {
    toast.error('Ajuste as posições das categorias no preview antes de salvar.')
    return
  }
  const slidePositions = Object.fromEntries(
    (
      [
        'leftBlockWidth',
        'titleY',
        'projectTypeY',
        'descriptionY',
        'bonusY',
        'priceY',
        'loyaltyY',
        'planTitleY',
        'serviceBlockX',
        'serviceBlockGap',
        'serviceBlockStartY',
      ] as const
    )
      .filter((key) => typeof params.layout[key] === 'number')
      .map((key) => [key, params.layout[key]]),
  )
  try {
    await api.patch(`/plans/${master.id}`, {
      config: {
        proposalLayout: { ...slidePositions, categoryCoords },
      },
    })
    toast.success(`Layout padrão salvo em "${master.nome}"`)
    params.scopeLayoutSignatureRef.current = ''
    await params.reloadCatalog({ silent: true })
  } catch (err: unknown) {
    toast.error(getFriendlyError(err, 'Não foi possível salvar o layout no plano'))
  }
}

export async function saveProposalAsTemplate(params: {
  deal: Deal
  proposalItems: PropostaItem[]
  comboVariations: ComboVariation[]
  layout: LayoutConfig
}): Promise<void> {
  if (params.proposalItems.length === 0) {
    toast.error('Adicione pelo menos um item para salvar como template')
    return
  }
  const nome = prompt('Nome do template:')
  if (!nome?.trim()) return
  try {
    await api.post('/propostas/templates', {
      nome: nome.trim(),
      tipo: (params.deal as Deal & { servico?: string }).servico || 'generico',
      items: params.proposalItems.map(normalizeItemSetupEntry),
      combos: params.comboVariations,
      layout: params.layout,
    })
    toast.success('Template salvo com sucesso!')
  } catch (err: unknown) {
    toast.error(getFriendlyError(err, 'Erro ao salvar template'))
  }
}
